import type { JsonValue } from "../types";

type JsonKind = "string" | "number" | "boolean" | "null" | "object" | "array";

const getJsonKind = (value: JsonValue): JsonKind => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
};

const canMatchArrayItem = (original: JsonValue, next: JsonValue): boolean => {
  if (getJsonKind(original) !== getJsonKind(next)) return false;
  if (original !== null && next !== null && typeof original === "object" && typeof next === "object") {
    if (Array.isArray(original) || Array.isArray(next)) return true;
    return Object.keys(original).every((key) => Object.prototype.hasOwnProperty.call(next, key));
  }
  return true;
};

const validateUpdate = (original: JsonValue, next: JsonValue, path: string): string | null => {
  if (getJsonKind(original) !== getJsonKind(next)) {
    return `Data type cannot change at ${path}`;
  }

  if (Array.isArray(original) && Array.isArray(next)) {
    let originalIndex = 0;
    for (let nextIndex = 0; nextIndex < next.length && originalIndex < original.length; nextIndex += 1) {
      if (!canMatchArrayItem(original[originalIndex], next[nextIndex])) continue;

      const error = validateUpdate(original[originalIndex], next[nextIndex], `${path}[${nextIndex}]`);
      if (error) return error;
      originalIndex += 1;
    }

    return null;
  }

  if (original !== null && next !== null && typeof original === "object" && typeof next === "object") {
    const nextObject = next as Record<string, JsonValue>;

    for (const [key, originalValue] of Object.entries(original as Record<string, JsonValue>)) {
      if (!Object.prototype.hasOwnProperty.call(nextObject, key)) {
        return `Existing key cannot be removed: ${path}.${key}`;
      }

      const error = validateUpdate(originalValue, nextObject[key], `${path}.${key}`);
      if (error) return error;
    }
  }

  return null;
};

export const validateUserSafeUpdate = (original: JsonValue, next: JsonValue): string | null =>
  validateUpdate(original, next, "root");